import React, { useContext, useEffect, useState } from 'react';
import { Collection } from '@amzn/awscat-pattern-library-service-model';
import {
  Box,
  Checkbox,
  CheckboxProps,
  ButtonProps,
  Icon,
  ExpandableSection,
  BoxProps,
} from '@amzn/awsui-components-core';

import CommunityCollectionCtx from './CommunityCollectionCtx';
import {
  Filters,
  FilterCategory,
  filterOptionToLabelMap,
  Filter,
} from '../../content/ContentFilters';
import {
  updateFilterOptionWithValue,
  getFiltersToApply,
  getFilterOptionsKeys,
  sortPublicationFilter,
} from '../../utils/ContentFilterUtils';
import { getCollecitonsInitialFilterOptions, applyFilterToCollection } from './utils';
import constants from '../../../common/constants';
import { CollectionWithTemplateMetaData } from './CommunityCollection';

const MAX_NUMBER_OF_FILTER = 8;
enum FilterVisibilityType {
  ViewMore = 'View more',
  ViewLess = 'View less',
}

interface Props {
  collections: CollectionWithTemplateMetaData[];
}

const CollectionFilters = ({ collections }: Props) => {
  const { filterOptions, setFilterOptions, setFilteredCollections } =
    useContext(CommunityCollectionCtx);
  const [filterVisibilityMap, setFilterVisibilityMap] = useState(
    new Map<string, FilterVisibilityType>(),
  );

  const updateFilterOptions = (initialFilterOptions: Filters) => {
    collections.forEach((item) => {
      Object.values(initialFilterOptions).forEach((filter) => {
        if (filter && Array.isArray(filter.values)) {
          const filterValuesSet = new Set<string>();
          filter.values.forEach((v) => {
            const filterValue =
              filter.metadata !== ''
                ? // @ts-expect-error TS7053 - strict migration
                  item[filter.metadata][v]
                : filter.mappedValues
                  ? // @ts-expect-error TS7053 - strict migration
                    filter.mappedValues.get(item[v])
                  : // @ts-expect-error TS7053 - strict migration
                    item[v];
            updateFilterOptionWithValue(filterValue, filter, filterValuesSet);
          });
        }
      });
    });
    const hasFavoriteCollectionFilter = collections.some((collection) => collection.hasRated);
    if (hasFavoriteCollectionFilter) {
      const favoriteCollectionsCount = collections.filter(
        (collection) => collection.hasRated,
      ).length;
      // @ts-expect-error TS18048 - strict migration
      initialFilterOptions.isFavoriteCollection.options.isFavoriteCollection = {
        count: favoriteCollectionsCount,
        // @ts-expect-error TS2322 - strict migration
        label: filterOptionToLabelMap.get(FilterCategory.IsFavoriteCollection),
        selected: false,
        value: FilterCategory.IsFavoriteCollection,
      };
    }
    return initialFilterOptions;
  };

  const getInitialFilterOptions = () => {
    const initialFilterOptions = getCollecitonsInitialFilterOptions([
      FilterCategory.IsFavoriteCollection,
    ]);
    return updateFilterOptions(initialFilterOptions);
  };

  useEffect(() => {
    setFilterOptions(getInitialFilterOptions());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFilterOptions({ ...getInitialFilterOptions() });
  }, [collections]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilter = (filterOptionsToApply: Filters) => {
    const filtersToApply = getFiltersToApply(filterOptionsToApply);
    const newFilteredCollections = filtersToApply.length
      ? collections.filter((collection) => applyFilterToCollection(collection, filtersToApply))
      : collections;
    setFilteredCollections([...newFilteredCollections]);
  };

  useEffect(() => {
    applyFilter(filterOptions);
  }, [filterOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // @ts-expect-error TS7006 - strict migration
  const expandSection = (filter: string, event) => {
    event.preventDefault();
    // @ts-expect-error TS2532 - strict migration
    filterOptions[filter].expanded = event.detail.expanded;
    setFilterOptions({ ...filterOptions });
  };

  const selectDeselectFilter = (
    filterSectionId: string,
    filterOptionKey: string,
    event: CustomEvent<CheckboxProps.ChangeDetail>,
  ) => {
    const selected = event.detail.checked;
    const filterSection = filterOptions[filterSectionId];

    // @ts-expect-error TS18048 - strict migration
    if (filterSection.options[filterOptionKey]) {
      // @ts-expect-error TS18048 - strict migration
      filterSection.options[filterOptionKey].selected = selected;
      setFilterOptions({ ...filterOptions });
    }
  };

  const renderOptionsToCheckbox = (filterSectionId: string, filter: Filter) => {
    const { options, displayCount } = filter;
    let filterOptionsKeys = getFilterOptionsKeys(options);
    filterOptionsKeys = sortPublicationFilter(filter, filterOptionsKeys, filterSectionId);
    return filterOptionsKeys.map((optionKey, index) => {
      const filterOption = options[optionKey];
      const count = displayCount ? `(${filterOption?.count})` : null;
      const padding: BoxProps.Spacing = [
        filterOptionToLabelMap.get(FilterCategory.RequireCode),
      ].includes(filterOption?.label)
        ? { left: 'xxl' }
        : { left: 'l' };
      const filterCheckBox = (
        <Box padding={padding} key={optionKey}>
          <Checkbox
            key={optionKey}
            // @ts-expect-error TS2322 - strict migration
            checked={filterOption?.selected}
            // @ts-expect-error TS2322 - strict migration
            onChange={selectDeselectFilter.bind(this, filterSectionId, optionKey)}
          >
            {filterOption?.label} {count}
          </Checkbox>
        </Box>
      );
      const currentVisibility = filterVisibilityMap.get(filterSectionId);
      const showViewMore =
        currentVisibility === FilterVisibilityType.ViewMore || !currentVisibility;
      return showViewMore && index >= MAX_NUMBER_OF_FILTER ? null : filterCheckBox;
    });
  };

  const handleViewMoreFilterClick = (
    filterSectionId: string,
    event: CustomEvent<ButtonProps.ClickDetail>,
  ) => {
    event.preventDefault();
    const currentVisibility = filterVisibilityMap.get(filterSectionId);
    switch (currentVisibility) {
      case FilterVisibilityType.ViewMore:
        filterVisibilityMap.set(filterSectionId, FilterVisibilityType.ViewLess);
        break;
      case FilterVisibilityType.ViewLess:
        filterVisibilityMap.set(filterSectionId, FilterVisibilityType.ViewMore);
        break;
      default:
        filterVisibilityMap.set(filterSectionId, FilterVisibilityType.ViewLess);
        break;
    }
    setFilterVisibilityMap(new Map(filterVisibilityMap));
  };

  const renderViewVisibilityOption = (filterOptionsKeys: string[], filterSectionId: string) => {
    const showVisibilityOption = filterOptionsKeys.length > MAX_NUMBER_OF_FILTER;
    if (!showVisibilityOption) {
      return null;
    }
    const currentVisibility = filterVisibilityMap.get(filterSectionId);
    const showViewMore = currentVisibility === FilterVisibilityType.ViewMore || !currentVisibility;
    return (
      <div
        // @ts-expect-error TS2322 - strict migration
        onClick={handleViewMoreFilterClick.bind(this, filterSectionId)}
        className="home-page-filter-view-more"
      >
        <Icon name={showViewMore ? 'treeview-expand' : 'treeview-collapse'} variant="link" />
        <Box variant="span" color="text-status-info" fontSize="body-s" padding={{ left: 'xs' }}>
          {showViewMore ? FilterVisibilityType.ViewMore : FilterVisibilityType.ViewLess}
        </Box>
      </div>
    );
  };

  const renderFilterToSections = () => {
    return Object.keys(filterOptions).map((filterSectionId) => {
      const filter = filterOptions?.[filterSectionId];
      const filterOptionsKeys = getFilterOptionsKeys(filter?.options || {});
      return filter && !filter.hide && Object.keys(filter.options).length ? (
        <ExpandableSection
          key={filterSectionId}
          expanded={filter.expanded}
          onChange={expandSection.bind(this, filter.filterCategory)}
          header={filter.label}
        >
          {renderOptionsToCheckbox(filterSectionId, filter)}
          {renderViewVisibilityOption(filterOptionsKeys, filterSectionId)}
        </ExpandableSection>
      ) : null;
    });
  };

  return (
    <Box variant="div" padding={{ top: 's' }}>
      <Box variant="h3" padding={{ left: 's' }}>
        {constants.FILTERS}
      </Box>
      {renderFilterToSections()}
    </Box>
  );
};

export default CollectionFilters;
